/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-16
 */

// Studio role hierarchy
export enum StudioRole {
  OWNER = 'owner',
  MANAGER = 'manager',
  ADMIN = 'admin',
  INSTRUCTOR = 'instructor',
  PARENT = 'parent',
  STUDENT = 'student',
  MEMBER = 'member',
}

// Subscription status
export enum SubscriptionStatus {
  ACTIVE = 'active',
  TRIAL = 'trial',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  PAST_DUE = 'past_due',
}

// Studio interface
export interface Studio {
  id: string;
  name: string;
  description: string;
  slug?: string; // URL-friendly name for public endpoints
  logo?: string;
  businessInfo?: BusinessInfo;
  settings?: StudioSettings;
  memberCount: number;
  member_count?: number; // Backend snake_case field
  subscriptionStatus: SubscriptionStatus;
  createdAt: string;
  created_at?: string; // Backend snake_case field
  updatedAt: string;
  updated_at?: string; // Backend snake_case field
  isPersonal: boolean;
  allowJoinRequests?: boolean;
  allow_join_requests?: boolean; // Backend snake_case field
  // Studio type from database
  studioType?: 'personal' | 'business' | 'organization';
  studio_type?: 'personal' | 'business' | 'organization'; // Backend snake_case field
  // Contact information fields (directly on studio object)
  email?: string;
  phone?: string;
  website?: string;
  address?:
    | string
    | {
        street_address?: string;
        street_address_2?: string;
        city?: string;
        state?: string;
        postal_code?: string;
        country?: string;
      };
  timezone?: string;
  // Enhanced activity fields from backend
  lastActivity?: string;
  last_activity?: string; // Backend snake_case field
  activityType?: string;
  activity_type?: string; // Backend snake_case field
  lastMembershipActivity?: string;
  last_membership_activity?: string; // Backend snake_case field
  lastMemberJoined?: string;
  last_member_joined?: string; // Backend snake_case field
}

// Business information
export interface BusinessInfo {
  address?: string;
  phone?: string;
  website?: string;
  taxId?: string;
}

// Studio settings
export interface StudioSettings {
  timezone: string;
  language: string;
  currency: string;
  dateFormat: string;
  isPublic: boolean;
}

// Studio membership
export interface StudioMembership {
  id: string;
  studioId: string;
  userId: string;
  role: StudioRole;
  status: MemberStatus;
  joinedAt: string;
  lastActivity?: string;
}

// Member status
export enum MemberStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
}

// Studio context
export interface StudioContext {
  currentStudio: Studio | null;
  userRole: StudioRole | null;
  permissions: StudioPermissions;
  switchedAt: string | null;
}

// Studio permissions
export interface StudioPermissions {
  canViewStudio: boolean;
  canEditStudio: boolean;
  canDeleteStudio: boolean;
  canInviteMembers: boolean;
  canViewMembers: boolean;
  canManageMembers: boolean;
  canViewBilling: boolean;
  canManageBilling: boolean;
  canAccessTools: boolean;
  canConfigureTools: boolean;
}

// Breadcrumb item
export interface BreadcrumbItem {
  label: string;
  path: string;
  icon?: string;
}

// API request/response types
export interface StudioListParams {
  search?: string;
  role?: StudioRole;
  subscriptionStatus?: SubscriptionStatus;
  ordering?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface CreateStudioRequest {
  name: string;
  description: string;
  email: string;
  businessInfo?: Partial<BusinessInfo>;
  isPersonal?: boolean;
  logo?: File;
  allowJoinRequests?: boolean;
  joinRequestMessage?: string;
}

export interface UpdateStudioRequest {
  name?: string;
  description?: string;
  businessInfo?: Partial<BusinessInfo>;
  settings?: Partial<StudioSettings>;
  timezone?: string;
  studioType?: 'personal' | 'business' | 'organization';
  allowJoinRequests?: boolean;
  joinRequestMessage?: string;
  logo?: File;
}

export interface SwitchStudioRequest {
  studio_id: string;
}

export interface SwitchStudioResponse {
  currentStudio: Studio;
  userRole: StudioRole;
  permissions: StudioPermissions;
  switchedAt: string;
}

// Studio roles API response
export interface StudioRoleOption {
  value: string;
  label: string;
}

export interface StudioRolesResponse {
  roles: StudioRoleOption[];
}
